import type { components } from '@adiox/api';
import type { AccountValuation, EngineEvent, HubEvent, OrderRecord, PositionRecord } from '@adiox/api';

type AccountSnapshot = components['schemas']['AccountSnapshot'];

/** An open position with its live figures. `price`, `profit` and `margin` are null until the first valuation. */
export interface Position {
  ticket: number;
  symbol: string;
  side: string;
  volume: number;
  priceOpen: number;
  stopLoss: number;
  takeProfit: number;
  swap: number;
  /** The commission charged when the position opened (already taken from the balance). */
  commission: number;
  /** The trailing stop distance in points; 0: off. The server keeps the stop loss that far behind the price. */
  trailingPoints: number;
  timeOpen: string;
  comment?: string;
  price: number | null;
  profit: number | null;
  margin: number | null;
}

export interface AccountFigures {
  balance: number;
  credit: number;
  equity: number;
  margin: number;
  freeMargin: number;
  marginLevel: number;
  floating: number;
  marginCall: boolean;
}

export interface AccountState {
  login: number;
  currency: string;
  leverage: number;
  /** The group's margin-call and stop-out levels (margin level, %); 0 when not known. */
  marginCallLevel: number;
  stopOutLevel: number;
  tradingEnabled: boolean;
  /** False until the first snapshot arrived and buffered events were applied (and again while a resync reloads it). */
  loaded: boolean;
  figures: AccountFigures;
  positions: Record<number, Position>;
  orders: Record<number, OrderRecord>;
  /** The highest event seq applied; events at or below it are ignored. */
  lastSeq: number;
}

export const emptyAccount = (login: number): AccountState => ({
  login,
  currency: '',
  leverage: 0,
  marginCallLevel: 0,
  stopOutLevel: 0,
  tradingEnabled: true,
  loaded: false,
  figures: {
    balance: 0,
    credit: 0,
    equity: 0,
    margin: 0,
    freeMargin: 0,
    marginLevel: 0,
    floating: 0,
    marginCall: false,
  },
  positions: {},
  orders: {},
  lastSeq: 0,
});

/** Replaces the state with a REST snapshot (the truth at the moment it was taken). */
export function applySnapshot(state: AccountState, snapshot: AccountSnapshot): AccountState {
  const positions: Record<number, Position> = {};
  for (const p of snapshot.positions) {
    positions[p.ticket] = {
      ticket: p.ticket,
      symbol: p.symbol,
      side: p.side,
      volume: p.volume,
      priceOpen: p.priceOpen,
      stopLoss: p.stopLoss,
      takeProfit: p.takeProfit,
      swap: p.swap,
      commission: p.commission,
      trailingPoints: p.trailingPoints ?? 0,
      timeOpen: p.timeOpen,
      price: p.priceCurrent,
      profit: p.profit,
      margin: p.margin,
    };
  }
  const orders: Record<number, OrderRecord> = {};
  for (const o of snapshot.orders) orders[o.ticket] = o;
  return {
    ...state,
    currency: snapshot.currency,
    leverage: snapshot.leverage,
    marginCallLevel: snapshot.marginCallLevel ?? 0,
    stopOutLevel: snapshot.stopOutLevel ?? 0,
    figures: {
      balance: snapshot.balance,
      credit: snapshot.credit,
      equity: snapshot.equity,
      margin: snapshot.margin,
      freeMargin: snapshot.freeMargin,
      marginLevel: snapshot.marginLevel,
      floating: snapshot.floating,
      marginCall: snapshot.marginCall,
    },
    positions,
    orders,
  };
}

/** Live figures and per-position prices from the `account` stream (only positions that exist are touched). */
export function applyValuation(state: AccountState, v: AccountValuation): AccountState {
  const positions = { ...state.positions };
  for (const live of v.positions) {
    const existing = positions[live.ticket];
    if (existing) {
      positions[live.ticket] = {
        ...existing,
        price: live.price,
        profit: live.profit,
        swap: live.swap,
        commission: live.commission ?? existing.commission,
      };
    }
  }
  return {
    ...state,
    figures: {
      balance: v.balance,
      credit: v.credit,
      equity: v.equity,
      margin: v.margin,
      freeMargin: v.freeMargin,
      marginLevel: v.marginLevel,
      floating: v.floating,
      marginCall: v.marginCall,
    },
    positions,
  };
}

function upsertPosition(state: AccountState, record: PositionRecord): AccountState {
  if (record.timeClose) {
    if (!(record.ticket in state.positions)) return state;
    const { [record.ticket]: _closed, ...rest } = state.positions;
    return { ...state, positions: rest };
  }
  const existing = state.positions[record.ticket];
  return {
    ...state,
    positions: {
      ...state.positions,
      [record.ticket]: {
        ticket: record.ticket,
        symbol: record.symbol,
        side: record.side,
        volume: record.volume,
        priceOpen: record.priceOpen,
        stopLoss: record.stopLoss,
        takeProfit: record.takeProfit,
        swap: record.swap,
        // the position event does not carry it: it comes with the snapshot and every valuation
        commission: existing?.commission ?? 0,
        trailingPoints: record.trailingPoints ?? 0,
        timeOpen: record.timeOpen,
        comment: record.comment,
        // live figures survive a modification (new SL/TP or a partial close); a new position waits for its first valuation
        price: existing?.price ?? null,
        profit: existing?.profit ?? null,
        margin: existing?.margin ?? null,
      },
    },
  };
}

function applyEngineEvent(state: AccountState, event: EngineEvent): AccountState {
  switch (event.type) {
    case 'position':
      return upsertPosition(state, event.position);
    case 'order': {
      if (event.order.status === 'Placed') {
        return { ...state, orders: { ...state.orders, [event.order.ticket]: event.order } };
      }
      if (!(event.order.ticket in state.orders)) return state;
      const { [event.order.ticket]: _done, ...rest } = state.orders;
      return { ...state, orders: rest };
    }
    case 'order_erased': {
      if (!(event.ticket in state.orders)) return state;
      const { [event.ticket]: _erased, ...rest } = state.orders;
      return { ...state, orders: rest };
    }
    case 'position_erased': {
      if (!(event.ticket in state.positions)) return state;
      const { [event.ticket]: _erased, ...rest } = state.positions;
      return { ...state, positions: rest };
    }
    case 'account':
      return {
        ...state,
        currency: event.account.currency,
        leverage: event.account.leverage,
        tradingEnabled: event.account.tradingEnabled,
        figures: { ...state.figures, balance: event.account.balance, credit: event.account.credit },
      };
    default:
      // deal, margin_call, stop_out and daily_snapshot change no state here (notifications and history use them)
      return state;
  }
}

/**
 * Applies one hub event. Events carry whole records, so applying them in order is idempotent; anything at or below the
 * highest applied seq is ignored, which makes a replay that overlaps live delivery harmless.
 */
export function applyEvent(state: AccountState, message: HubEvent): AccountState {
  if (message.seq <= state.lastSeq) return state;
  return { ...applyEngineEvent(state, message.event), lastSeq: message.seq };
}

export const sortedPositions = (state: AccountState): Position[] =>
  Object.values(state.positions).sort((a, b) => a.ticket - b.ticket);

export const sortedOrders = (state: AccountState): OrderRecord[] =>
  Object.values(state.orders).sort((a, b) => a.ticket - b.ticket);
